import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref, watch } from "vue";
import { useAppliedBlueprints } from "@/features/blueprints/api/use-blueprints";
import { useChannelFunnels } from "@/features/channels/api/use-channel-funnels";
import { publicChatUrl, useChannels } from "@/features/channels/api/use-channels";
import { useThreads } from "@/features/inbox/api/use-inbox";
import { useGaps } from "@/features/memory/api/use-gaps";
import { onboardingKeys, useOnboardingState } from "@/features/onboarding/api/use-onboarding";
import { useSites } from "@/features/site/api/use-site";
import { useStructure } from "@/features/structure/api/use-structure";
import { useBusiness } from "@/features/workspace/api/use-workspace";
import { homeSteps } from "./home-steps";

/** The home and the guided journey use the same completion rules and query cache. */
export function useHomeSteps() {
	const { data: entities } = useStructure();
	const { data: applied } = useAppliedBlueprints();
	const { data: channels } = useChannels();
	const { data: funnels } = useChannelFunnels();
	const { data: threads } = useThreads({ status: "all", assignment: "anyone", search: "" });
	const { data: gaps } = useGaps(ref("open"));
	const { data: onboarding } = useOnboardingState();
	const { data: sites } = useSites();
	const { data: business } = useBusiness();
	const chat = computed(() =>
		(channels.value ?? []).find((c) => c.type === "webchat" && c.isPublic && c.publicSlug),
	);
	// As conversas DESTE chat: é a de teste que fecha a etapa, não uma do WhatsApp.
	const { data: chatThreads } = useThreads({
		status: "all",
		assignment: "anyone",
		search: "",
		channelId: () => chat.value?.id ?? null,
	});
	// Uma conversa assumida agora: o estado relê o fato durável antes de um "Devolver".
	const client = useQueryClient();
	watch(
		() => (threads.value ?? []).some((t) => t.assignedTo !== null),
		(held) => {
			if (held && onboarding.value?.threadAssumed === false)
				void client.invalidateQueries({ queryKey: onboardingKeys.state });
		},
	);
	const steps = computed(() =>
		homeSteps({
			identityOnly: onboarding.value?.identityOnly,
			hasStructure:
				(applied.value ?? []).length > 0 ||
				(entities.value ?? []).some((e) => e.status === "active"),
			onboardingPhase: onboarding.value?.phase ?? null,
			publishedChatUrl: chat.value?.publicSlug ? publicChatUrl(chat.value.publicSlug) : null,
			publishedChatAnswerer: chat.value?.metadata?.agentCode ?? null,
			chatFunnelLinked: chat.value
				? funnels.value
					? funnels.value.linksOf(chat.value.id).some((l) => l.isActive)
					: null
				: false,
			knowsTheBusiness: Boolean(business.value?.businessProfile?.trim()),
			openGaps: gaps.value ? gaps.value.rows.length : null,
			hasThreads: (threads.value ?? []).length > 0,
			// Já assumida uma vez conta: o "Devolver" que o passo ensina não o desfaz (03/11).
			hasAssumedThread:
				onboarding.value?.threadAssumed === true ||
				(threads.value ?? []).some((t) => t.assignedTo !== null),
			// Publicado: o site no ar (o fim da trilha mostra-o); a etapa fecha já ao pedir, em `siteRequested`.
			sitePublished: (sites.value ?? []).some((s) => s.isPublished),
			chatTested: !!chat.value && (chatThreads.value ?? []).length > 0,
			siteRequested: !!onboarding.value?.siteBuild || (sites.value ?? []).length > 0,
		}),
	);
	return { steps, entities };
}
